// TableBody.tsx
import { Fragment, useState } from "react";
import Image from "../images/Image";
import { TableDetailsItem } from "./tableFunctions";
import moment from "moment";
import CustomModal from "../CustomModal";
import { adminContents } from "../../pages/AdminManagement/components/AdminManagementContent";
import useTranslation from "../CustomHooks/useTranslation";
import { showToast } from "../toastMessage/toastSlice";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import config from "../../config/config";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { convertTimestamp, formatDateToDDMMYYYY } from "../commonfunctions/convertDate";
import { tab } from "@testing-library/user-event/dist/cjs/convenience/tab.js";

interface TableBodyProps {
  tableBodyItem: Record<string, any>; // Flexible structure for dynamic keys
  TableDetails: TableDetailsItem; // Generalized structure for table details
  tableBodyIndex: number;
  getList?: () => void;
  handleActions: (id: string, key: string, text?: string) => void;
  hasActivateDeactivateMDPrivilege?: boolean;
  hasViewMDPrivilege?: boolean;
  hasEditMDPrivilege?: boolean;
  mnoList?: boolean;
  listData?: unknown[];
  mainEndPoint?: string;
}

const TableBody = ({
  tableBodyItem,
  TableDetails,
  tableBodyIndex,
  handleActions,
  getList,
  hasActivateDeactivateMDPrivilege,
  hasViewMDPrivilege,
  hasEditMDPrivilege,
  mnoList,
  listData,
  mainEndPoint
}: TableBodyProps) => {
  const tableHeaders = Object.entries(TableDetails);
  const [isOpen, setIsOpen] = useState(false);
  let { activeList } = useSelector((state: RootState) => state.activeList);
  let targetStatus = tableBodyItem['target_achieved_status'] === 'Achieved';
  let targetNotStarted = tableBodyItem['target_achieved_status'] === 'Not Started';
  let isCustomer = tableBodyItem['user_role'] === 'Customer';
  const dispatch = useDispatch();
  const { translate } = useTranslation(adminContents);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const handleStatus = (text?: string) => {
    setButtonLoading(true);
    try {
      handleActions(tableBodyItem.id, "status", text);
    } catch (error: any) {
      dispatch(
        showToast({
          type: "error",
          message: error.message,
        })
      );
    } finally {
      setTimeout(() => {
        setIsOpen(false);
        // if (getList) getList();
        setButtonLoading(false);
      }, 1000);
    }
  };
  const getCustomData = (custom: string[], id?: string) => {
    let customElement = "";
    custom.forEach((item, index = 0) => {
      switch (item) {
        case "edited_with_compliance":
          if (tableBodyItem[item]) {
            customElement = 'Yes';
          } else {
            customElement = "No";
          }
          break;
        case "updated_at":
          if (tableBodyItem[item]) {
            customElement = moment(tableBodyItem[item]).format(
              "DD-MM-YYYY"
            );
          } else {
            customElement = "-";
          }
          break;
        case "last_logged_in":
        case "action_taken_at":
        case "transaction_date":
          if (tableBodyItem[item]) {
            customElement = moment(tableBodyItem[item]).format(
              "DD-MM-YYYY, hh:mm A"
            );
          } else {
            customElement = "-";
          }
          break;
        case "created_at":
          if (tableBodyItem[item]) {
            customElement = convertTimestamp(tableBodyItem[item]);
          } else {
            customElement = "-";
          }
          break;
        case "advance_amount":
        case "payable_amount":
          if (tableBodyItem[item]) {
            customElement = `AFA ${Number(tableBodyItem[item]).toFixed(2)}`;
          } else {
            customElement = "-";
          }
          break;
        case "onboarded_by_user_details":
          if (tableBodyItem[item]) {
            customElement = `${tableBodyItem[item].first_name} ${tableBodyItem[item].last_name} ${tableBodyItem[item].user_code ? `(${tableBodyItem[item].user_code})` : ''}`;
          } else {
            customElement = "-";
          }
          break;
        case "on_target_service_fee_percentage":
          if (tableBodyItem[item]) {
            customElement = `${Number(tableBodyItem[item]).toFixed(2).replace(/\.00$/, '')}%`;
          } else {
            customElement = "-";
          }
          break;
        case "target_achieved":
        case "achievement_amount_received":
          if (targetStatus && tableBodyItem[item]) {
            customElement = `AFA ${Number(tableBodyItem[item]).toFixed(2)}`;
          } else if (!targetStatus && tableBodyItem[item]) {
            customElement = tableBodyItem[item];
          } else {
            customElement = "-";
          }
          break;
        case "service_fee_received_amount":
        case "service_fee_paid_amount":
        case "target_amount":
          if (tableBodyItem[item] && Number(tableBodyItem[item]) !== 0.00) {
            customElement = `AFA ${Number(tableBodyItem[item]).toFixed(2)}`;
          } else {
            customElement = "-";
          }
          break;
        case "custom_status":
          if (tableBodyItem.status) {
            customElement = tableBodyItem.status;
          } else {
            customElement = "-";
          }
          break;
        case "first_name":
        case "last_name":
        case "country_code":
        case "cashier_phone_number":
        case "phone_number":
          if (tableBodyItem[item] !== null) {
            customElement += `${tableBodyItem[item]} `;
          }
          break;
        case "province":
        case "district":
        case "country":
          if (!tableBodyItem.province && !tableBodyItem.region && !tableBodyItem.country) {
            customElement = "-";
          } else {
            if (item !== 'district') {
              customElement += `${tableBodyItem[item]}, `;
            } else {
              customElement += `${tableBodyItem[item]}`;
            }
          }
          break;        
        default:
          if (tableBodyItem[item] !== null) {
            customElement += `${tableBodyItem[item]}, `;
          }
          if (index === custom.length - 1) {
            customElement = customElement.slice(0, -2);
          }
      }
    });
    return typeof customElement === 'string' ? customElement?.trim() : customElement;
  };

  const handleCustomAction = (key: string, type: string) => {
    switch (key) {
      case 'className':
        if (listData?.[tableBodyIndex]?.status === 'Approved') {
          return 'text-p_primary cursor-pointer'
        } else {
          return 'text-secondary_text'
        }
      case 'action':
        if (listData?.[tableBodyIndex]?.status === 'Approved') {
          return false
        } else {
          return true
        }
      default:
        break;
    }
  }
  return (
    <>
      {tableHeaders.map(([key, details], index = 0) => (
        <Fragment key={key}>
          {details.id === "actions" ? (
            <td className={`text-body2 sticky right-0 border-b bg-white `}>
              <div className={`px-4 py-2  ${details?.custom?.includes("text") ? 'tableBorderBody' : (activeList === 'Pending' || mainEndPoint==='cash-in') ? 'tableBorderPendingList' : 'tableBorderBody'}  flex justify-center`}>
                <div className="flex gap-[14px]">
                  {details?.custom?.includes("text") ? <p
                    className={`z-50 text-large  ${handleCustomAction('className', details?.custom?.includes("text") ? 'text' : '')}`}
                    onClick={() => { handleCustomAction('action', details?.custom?.includes("text") ? 'text' : '') ? {} : handleActions(tableBodyItem.id, details.actionTypes[0], tableBodyItem) }}
                  >{details.actionTypes[0]}</p> :
                    (() => {
                      // First, determine which actions are actually visible
                      const visibleActions = details.actionTypes.filter((imgItem: string) => {
                        if ((imgItem === "edit" || imgItem === "entry") && targetStatus) return false;
                        if (imgItem === "entry" && targetNotStarted) return false;
                        if (imgItem === "view") return hasViewMDPrivilege;
                        if (imgItem === "edit" || imgItem === 'payment' || imgItem === 'entry') return hasEditMDPrivilege;
                        if (imgItem === "upgrade") return hasEditMDPrivilege && tableBodyItem.credit_status === false;
                        return false;
                      });

                      // Now render each action with position information
                      return details.actionTypes.map((imgItem: string) => {
                        // Check if this action should be visible
                        const isActionVisible =
                          !((imgItem === "edit" || imgItem === "entry") && targetStatus) &&
                          !(imgItem === "entry" && targetNotStarted) &&
                          ((imgItem === "view" && hasViewMDPrivilege) ||
                            ((imgItem === "edit" || imgItem === 'payment' || imgItem === 'entry') && hasEditMDPrivilege) ||
                            (imgItem === "upgrade" && hasEditMDPrivilege && tableBodyItem.credit_status === false));

                        // Find the position of this action in the visible actions array
                        const actionPosition = visibleActions.indexOf(imgItem);

                        // Tooltip position information
                        const tooltipPosition = isActionVisible ? {
                          isFirst: actionPosition === 0,
                          isLast: actionPosition === visibleActions.length - 1,
                          total: visibleActions.length,
                          position: actionPosition + 1
                        } : undefined;

                        if ((imgItem === "edit" || imgItem === "entry") && targetStatus || (imgItem === "entry" && targetNotStarted)) {
                          return (
                            <Image
                              testId={`${imgItem}-btn-${tableBodyIndex}`}
                              src={`${imgItem.toLocaleLowerCase()}DisImg`}
                              key={imgItem}
                              onClick={() => { }}
                              className={`z-50 cursor-not-allowed`}
                              tableActions={true}
                              index={tableBodyIndex}
                              tooltiptype={imgItem}
                              tooltipPosition={tooltipPosition}
                            />
                          );
                        } else if ((imgItem === "view" && hasViewMDPrivilege) ||
                          ((imgItem === "edit" || imgItem === 'payment' || imgItem === 'entry') && hasEditMDPrivilege)) {
                          // if (isCustomer && imgItem === "view") {
                          //   return (
                          //     <Image
                          //       testId={`${imgItem}-btn-${tableBodyIndex}`}
                          //       src={`${imgItem.toLocaleLowerCase()}Img`}
                          //       key={imgItem}
                          //       className="z-50 cursor-not-allowed"
                          //       tableActions={true}
                          //       index={tableBodyIndex}
                          //       tooltiptype={imgItem}
                          //       tooltipPosition={tooltipPosition}
                          //     />
                          //   );
                          // }
                          return (
                            <Image
                              testId={`${imgItem}-btn-${tableBodyIndex}`}
                              src={`${imgItem.toLocaleLowerCase()}Img`}
                              key={imgItem}
                              className="z-50 cursor-pointer"
                              onClick={() => handleActions(tableBodyItem.id, imgItem, tableBodyItem)}
                              tableActions={true}
                              index={tableBodyIndex}
                              tooltiptype={imgItem}
                              tooltipPosition={tooltipPosition}
                            />
                          );
                        } else if (imgItem === "upgrade" && hasEditMDPrivilege) {
                          return (
                            <Image
                              testId={`${imgItem}-btn-${tableBodyIndex}`}
                              src={tableBodyItem.credit_status === true ? 'upgrade-disabledImg' : `${imgItem.toLocaleLowerCase()}Img`}
                              key={imgItem}
                              onClick={tableBodyItem.credit_status === false ? () => handleActions(tableBodyItem.id, imgItem) : () => { }}
                              className={`z-50 ${tableBodyItem.credit_status === true ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                              tableActions={true}
                              tooltipPosition={tooltipPosition}
                            />
                          );
                        } else {
                          return (
                            <Image
                              testId={`${imgItem}-btn-${tableBodyIndex}`}
                              src={`${imgItem.toLocaleLowerCase()}Img`}
                              key={imgItem}
                              className="z-50 cursor-not-allowed"
                              tableActions={true}
                            />
                          );
                        }
                      });
                    })()
                  }
                </div>
              </div>
            </td>
          ) : details.id === "status" ? (
            <td
              className={`text-body2 p-1.5 
                ${tableBodyIndex === 0 ? "border-t-0" : ""} 
                ${index === tableHeaders.length - 2 ? "border-y" : "border"}  
                border-primary_text ${details.className || ""}`}
            >
              <div className="status custom-toggle flex items-center relative">
                {hasActivateDeactivateMDPrivilege && (
                  <label className="switch" data-testid={`${tableBodyItem[details.id] ? `active-${tableBodyIndex}` : `inactive-${tableBodyIndex}`}`}>
                    <input
                      type="checkbox"
                      checked={tableBodyItem[details.id]}
                      onClick={() => setIsOpen(true)}
                    />
                    <span
                      id="user_status"
                      className="slider round"
                      data-testid={`toggle_${tableBodyIndex}`}
                    ></span>
                  </label>
                )}
                <p
                  id="status"
                  className={`disable_switch ${hasActivateDeactivateMDPrivilege ? "ml-3" : ""
                    } -mt-1`}
                >
                  {tableBodyItem[details.id]
                    ? translate("Active")
                    : translate("Inactive")}
                </p>
              </div>
            </td>
          ) : details.id === "credit_status" ? (
            <td className="border-b border-primary_text text-body2 p-1.5">
              <p
                id="credit-status"
                className={``}
              >
                {tableBodyItem[details.id]
                  ? translate("Received")
                  : translate("Pending")}
              </p>
            </td>
          ) : details.id === "receipt_url" ? (
            <td className="border-b border-r border-primary_text text-body2 p-1.5">
              <p
                id="transaction-reciept"
                className={`flex items-center justify-between`}
              >
                <div
                  className="cursor-pointer underline flex items-center gap-2 text-p_primary"
                  onClick={() => {
                    const fileUrl = config?.cdnURL + 'public/' + tableBodyItem[details.id];
                    window.open(fileUrl, "_blank");
                  }}
                >
                  <span>Uploaded Receipt</span>
                  <Image src="link" className="w-5 h-5" />
                </div>
              </p>
            </td>
          ) : (
            <td
              className={`${(details.id === 'contract_end_date' && new Date(tableBodyItem.contract_end_date) < new Date()) || (new Date(tableBodyItem.contract_end_date) < new Date() && details.id !== 'actions' && details.id !== 'status') ? 'text-negative text-sm font-normal' : 'text-body2'} ${mainEndPoint?.includes('mnos/service-fee') ? 'p-2' : 'p-1.5'} 
                ${tableBodyIndex === 0 ? "border-t-0" : ""} 
                ${index === tableHeaders.length - 2 ? "border-y" : "border"}  
                border-primary_text ${details.className || ""}`}
              title={
                details.custom
                  ? getCustomData(details.custom)
                  : tableBodyItem[details.id] ?? "-"
              }
            >
              <span className="more-lines relative group">
                {details.custom
                  ? getCustomData(details.custom, details.id)
                  : (details.id === 'contract_start_date' || details.id === 'contract_end_date') && tableBodyItem[details.id]
                    ? new Date(tableBodyItem[details.id]).toLocaleDateString('en-GB').replace(/\//g, '-')
                    : tableBodyItem[details.id] ?? "-"}
              </span>            </td>)}
        </Fragment>
      ))}

      {isOpen && (
        <CustomModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title={tableBodyItem?.status ? translate("Confirm Deactivation") : translate("Confirm Activation")}
          subText={
            tableBodyItem?.status
              ? translate("This action will revoke access to all features and tasks")
              : translate("This action will grant access to features and tasks")
          }
          primaryButtonText={tableBodyItem?.status ? translate('Deactivate') : translate("Activate")}
          secondaryButtonText={translate("Cancel")}
          handelSubmit={(text) => handleStatus(text)}
          buttonColor={tableBodyItem?.status ? 'bg-negative' : "bg-positive"}
          textArea={tableBodyItem?.status}
          buttonLoading={buttonLoading}
          buttonWidth="w-[108px]"
        />
      )}
    </>
  );
};

export default TableBody;
